import React, { useEffect } from "react";
import {
  Grid,
  Icon,
  IconButton,
  InputAdornment,
  MenuItem,
  TextField,
  Typography,
} from "@material-ui/core";
import { Field } from "formik";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import {
  addNewProvision,
  fillProvisionField,
  removeProvision,
  resetProvisionForm,
  setTheAgentForm,
} from "../store/agent-form.slice";
import { AppState } from "../../../store";
import { createAgentsStyles } from "../create/create-agents.styles";
import useAuthority from "app/hooks/useAuthority";
import { ROLES } from "app/constants/roles";

interface ProvisionInfoProps {
  errors?: any;
  touched?: any;
}

const ProvisionInfo = ({
  errors = {},
  touched = {},
}: ProvisionInfoProps) => {
  const dispatch = useDispatch();
  const agentForm = useSelector((state: AppState) => state.agents.agentForm);
  const classes = createAgentsStyles();
  const { t } = useTranslation();

  const { currencies } = useSelector(
    (state: AppState) => state.flights.currenciesList
  );

  const { hasUserAnyAuthority } = useAuthority();
  const isAdmin = hasUserAnyAuthority([ROLES.ROLE_ADMIN]);

  const getDefaultCurrency = () => {
    const defaultCurrency = currencies?.find((item) => item.isoCode === "EUR");
    return defaultCurrency ? defaultCurrency.id : "";
  };

  useEffect(() => {
    if (!currencies?.length) return;

    const first = agentForm.provisionForms?.[0];
    if (!first) return;

    if (first.currencyId === "") {
      dispatch(
        fillProvisionField({
          id: first.id,
          field: "currencyId",
          value: getDefaultCurrency(),
        })
      );
    }
  }, [currencies, agentForm.provisionForms, dispatch]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    id: string
  ) => {
    if (!isAdmin) return;

    dispatch(
      fillProvisionField({
        value: e.target.value,
        field: e.target.name,
        id,
      })
    );
  };

  const provisionFormTypes = [
    {
      label: t("app.main.agency.create.provisionInfo.label1", "By percentage"),
      value: "PERCENTAGE",
    },
    {
      label: t("app.main.agency.create.provisionInfo.label2", "By % and minimum"),
      value: "PERCENTAGE_MINIMUM",
    },
    {
      label: t("app.main.agency.create.provisionInfo.label3", "By % and maximum"),
      value: "PERCENTAGE_MAXIMUM",
    },
    {
      label: t(
        "app.main.agency.create.provisionInfo.label4",
        "By % and minimum, maximum"
      ),
      value: "PERCENTAGE_MINIMUM_MAXIMUM",
    },
    {
      label: t("app.main.agency.create.provisionInfo.label5", "With FIXED provision"),
      value: "FIXED",
    },
  ];

  const gridSize =
    agentForm.provisionFormType === "PERCENTAGE_MINIMUM_MAXIMUM" ? 2 : 3;

  const preventInvalidNumberInput = (e: any) => {
    if (
      e.key === "-" ||
      e.key === "e" ||
      e.key === "E" ||
      e.code === "Minus"
    ) {
      e.preventDefault();
    }
  };

  const getFieldError = (index: number, field: string) => {
    return (
      touched?.provisionForms?.[index]?.[field] &&
      errors?.provisionForms?.[index]?.[field]
    );
  };

  return (
    <div>
      <div
        style={{
          backgroundColor: "#e9e9e9",
          marginTop: "16px",
          padding: "30px 16px",
        }}
      >
        <Grid container spacing={4}>
          <Grid item xs={12}>
            <div
              className={classes.radioGroupDiv}
              role="group"
              aria-labelledby="my-radio-group"
            >
              <Typography style={{ marginBottom: ".5rem" }}>
                {t(
                  "app.main.agency.create.provisionInfo.label6",
                  "Form of provision:"
                )}
              </Typography>

              {provisionFormTypes.map(({ label, value }) => (
                <label key={value} style={{ marginRight: "16px" }}>
                  <Field
                    type="radio"
                    name="radioButton"
                    checked={agentForm.provisionFormType === value}
                    value={value}
                    disabled={!isAdmin}
                    onChange={() => {
                      if (!isAdmin) return;

                      dispatch(
                        setTheAgentForm({
                          ...agentForm,
                          provisionFormType: value,
                        })
                      );

                      dispatch(resetProvisionForm(getDefaultCurrency()));
                    }}
                  />
                  {label}
                </label>
              ))}
            </div>
          </Grid>
        </Grid>
      </div>

      <div
        style={{
          backgroundColor: "#f9f9f9",
          marginBottom: "16px",
          padding: "30px 16px",
        }}
      >
        {agentForm.provisionForms.map((item, index) => {
          return (
            <Grid container spacing={4} key={item.id}>
              {agentForm.provisionFormType !== "FIXED" && (
                <Grid item xs={gridSize}>
                  <Field
                    as={TextField}
                    style={{ width: "100%", marginRight: "20px" }}
                    label={t(
                      "app.main.agency.create.provisionInfo.label7",
                      "Provision from 0% - 20%"
                    )}
                    onChange={(
                      e: React.ChangeEvent<
                        HTMLInputElement | HTMLTextAreaElement
                      >
                    ) => {
                      handleChange(e, item.id);
                    }}
                    variant="outlined"
                    size="small"
                    name="provisionPercentage"
                    value={item.provisionPercentage}
                    type="number"
                    disabled={!isAdmin}
                    required
                    error={Boolean(getFieldError(index, "provisionPercentage"))}
                    helperText={getFieldError(index, "provisionPercentage")}
                    InputProps={{
                      inputProps: { min: 0, max: 20 },
                      endAdornment: (
                        <InputAdornment position="start">%</InputAdornment>
                      ),
                    }}
                    onKeyDown={preventInvalidNumberInput}
                  />
                </Grid>
              )}

              {(agentForm.provisionFormType === "PERCENTAGE_MINIMUM" ||
                agentForm.provisionFormType ===
                  "PERCENTAGE_MINIMUM_MAXIMUM") && (
                <Grid item xs={gridSize}>
                  <Field
                    as={TextField}
                    style={{ width: "100%", marginRight: "20px" }}
                    label={t(
                      "app.main.agency.create.provisionInfo.label8",
                      "Minimum provision"
                    )}
                    onChange={(
                      e: React.ChangeEvent<
                        HTMLInputElement | HTMLTextAreaElement
                      >
                    ) => {
                      handleChange(e, item.id);
                    }}
                    type="number"
                    value={item.minimumProvision}
                    InputProps={{ inputProps: { min: 0 } }}
                    variant="outlined"
                    size="small"
                    name="minimumProvision"
                    disabled={!isAdmin}
                    required
                    error={Boolean(getFieldError(index, "minimumProvision"))}
                    helperText={getFieldError(index, "minimumProvision")}
                    onKeyDown={preventInvalidNumberInput}
                  />
                </Grid>
              )}

              {(agentForm.provisionFormType === "PERCENTAGE_MAXIMUM" ||
                agentForm.provisionFormType ===
                  "PERCENTAGE_MINIMUM_MAXIMUM") && (
                <Grid item xs={gridSize}>
                  <Field
                    as={TextField}
                    style={{ width: "100%", marginRight: "20px" }}
                    label={t(
                      "app.main.agency.create.provisionInfo.label9",
                      "Maximum provision"
                    )}
                    onChange={(
                      e: React.ChangeEvent<
                        HTMLInputElement | HTMLTextAreaElement
                      >
                    ) => {
                      handleChange(e, item.id);
                    }}
                    type="number"
                    value={item.maximumProvision}
                    InputProps={{ inputProps: { min: 0 } }}
                    variant="outlined"
                    size="small"
                    name="maximumProvision"
                    disabled={!isAdmin}
                    required
                    error={Boolean(getFieldError(index, "maximumProvision"))}
                    helperText={getFieldError(index, "maximumProvision")}
                    onKeyDown={preventInvalidNumberInput}
                  />
                </Grid>
              )}

              {agentForm.provisionFormType === "FIXED" && (
                <Grid item xs={3}>
                  <Field
                    as={TextField}
                    style={{ width: "100%", marginRight: "20px" }}
                    label={t(
                      "app.main.agency.create.provisionInfo.label10",
                      "Provizioni për fluturim"
                    )}
                    onChange={(
                      e: React.ChangeEvent<
                        HTMLInputElement | HTMLTextAreaElement
                      >
                    ) => {
                      handleChange(e, item.id);
                    }}
                    type="number"
                    value={item.fixedProvision}
                    InputProps={{ inputProps: { min: 0 } }}
                    variant="outlined"
                    size="small"
                    name="fixedProvision"
                    disabled={!isAdmin}
                    required
                    error={Boolean(getFieldError(index, "fixedProvision"))}
                    helperText={getFieldError(index, "fixedProvision")}
                    onKeyDown={preventInvalidNumberInput}
                  />
                </Grid>
              )}

              {agentForm.provisionFormType !== "FIXED" && (
                <Grid item xs={gridSize}>
                  <Field
                    as={TextField}
                    style={{ width: "100%", marginRight: "20px" }}
                    label={t(
                      "app.main.agency.create.provisionInfo.label11",
                      "Llogaritni NETO mbi çmimin"
                    )}
                    onChange={(
                      e: React.ChangeEvent<
                        HTMLInputElement | HTMLTextAreaElement
                      >
                    ) => {
                      handleChange(e, item.id);
                    }}
                    type="number"
                    value={item.neto}
                    InputProps={{ inputProps: { min: 0 } }}
                    variant="outlined"
                    size="small"
                    name="neto"
                    disabled={!isAdmin}
                    required
                    error={Boolean(getFieldError(index, "neto"))}
                    helperText={getFieldError(index, "neto")}
                    onKeyDown={preventInvalidNumberInput}
                  />
                </Grid>
              )}

              <Grid item xs={3}>
                <div style={{ display: "flex" }}>
                  <Field
                    as={TextField}
                    onChange={(
                      e: React.ChangeEvent<
                        HTMLInputElement | HTMLTextAreaElement
                      >
                    ) => {
                      handleChange(e, item.id);
                    }}
                    variant="outlined"
                    value={item.currencyId}
                    name="currencyId"
                    label={t("app.main.report.search.currency", "Currency")}
                    select
                    style={{ flex: 1, minWidth: 120 }}
                    size="small"
                    disabled={!isAdmin}
                    required
                    error={Boolean(getFieldError(index, "currencyId"))}
                    helperText={getFieldError(index, "currencyId")}
                  >
                    {currencies &&
                      currencies
                        .filter((cr) =>
                          ["EUR", "CHF", "USD"].includes(cr.isoCode)
                        )
                        .map((cr: any, idx) => (
                          <MenuItem key={idx} value={cr.id}>
                            {cr.name} - {cr.isoCode}
                          </MenuItem>
                        ))}
                  </Field>

                  <IconButton
                    style={{ margin: "0 10px" }}
                    onClick={() => {
                      if (!isAdmin) return;
                      dispatch(addNewProvision(getDefaultCurrency()));
                    }}
                    size="small"
                    disabled={!isAdmin}
                  >
                    <Icon>add</Icon>
                  </IconButton>

                  <IconButton
                    onClick={() => {
                      if (!isAdmin) return;
                      dispatch(removeProvision(item.id));
                    }}
                    size="small"
                    disabled={
                      !isAdmin || agentForm.provisionForms.length === 1
                    }
                  >
                    <Icon color="error">delete</Icon>
                  </IconButton>
                </div>
              </Grid>
            </Grid>
          );
        })}
      </div>
    </div>
  );
};

export default ProvisionInfo;